/**
 * Zod schemas for package-trust domain — GH-236.
 *
 * Exports:
 *   trustStatusSchema        — enum schema for the 3 trust statuses
 *   confidenceRollupSchema   — validates item-level confidence counts
 *   ambiguityItemSchema      — validates a single ambiguous item
 *   trustSummarySchema       — validates the full GET /trust response shape
 *
 * Design notes:
 *   - All count fields in confidenceRollupSchema are non-negative integers.
 *   - ambiguityItemSchema enforces reasons array has at least one entry.
 *   - trustSummarySchema nests approvalRowSchema for the approvals array.
 *   - TRUST_STATUSES tuple drives the trustStatusSchema enum (no string duplication).
 */

import { z } from 'zod'
import { TRUST_STATUSES } from '../constants'
import { approvalRowSchema } from './package-approval'

// ── Trust status schema ────────────────────────────────────────────────────────

/**
 * Validates a TrustStatus value against the 3 canonical states.
 * Driven by TRUST_STATUSES const tuple to prevent drift.
 */
export const trustStatusSchema = z.enum(TRUST_STATUSES)

export type TrustStatusSchema = z.infer<typeof trustStatusSchema>

// ── Confidence rollup schema ───────────────────────────────────────────────────

/**
 * Validates item-level confidence counts aggregated across all batches.
 * All counts must be non-negative integers.
 */
export const confidenceRollupSchema = z.object({
  /** Items scored high-confidence. */
  high: z.number().int().min(0),
  /** Items scored medium-confidence. */
  medium: z.number().int().min(0),
  /** Items scored low-confidence (flagged as ambiguous). */
  low: z.number().int().min(0),
  /** Items not yet confidence-scored. */
  unreviewed: z.number().int().min(0),
})

export type ConfidenceRollupSchema = z.infer<typeof confidenceRollupSchema>

// ── Ambiguity item schema ──────────────────────────────────────────────────────

/**
 * Validates a single ambiguous item surfaced by the ambiguity queue.
 *
 * reasons must have at least one entry; unknown reason strings are rejected.
 * filename is optional and nullable (not all items have an associated filename).
 */
export const ambiguityItemSchema = z.object({
  /** FK → ingest_items.id */
  itemId: z.string().min(1),
  /** FK → ingest_batches.id */
  batchId: z.string().min(1),
  /** Original filename; optional, nullable. */
  filename: z.string().nullable().optional(),
  /**
   * Why this item is flagged. At least one reason required.
   * Values: low_confidence | cluster_conflict.
   */
  reasons: z.array(
    z.enum(['low_confidence', 'cluster_conflict'])
  ).min(1),
  /** Current item lifecycle status. */
  status: z.string().min(1),
})

export type AmbiguityItemSchema = z.infer<typeof ambiguityItemSchema>

// ── Trust summary schema ───────────────────────────────────────────────────────

/**
 * Validates the full trust summary returned by GET /api/onboarding-packages/:id/trust.
 *
 * Composite read shape — all fields required.
 * Nests confidenceRollupSchema inside confidence.items.
 * Nests approvalRowSchema[] for the approvals array.
 * Nests ambiguityItemSchema[] for the ambiguities array.
 */
export const trustSummarySchema = z.object({
  /** FK → onboarding_packages.id */
  packageId: z.string().min(1),
  /** Derived composite trust state. */
  status: trustStatusSchema,
  /** Item-level confidence breakdown. */
  confidence: z.object({
    items: confidenceRollupSchema,
  }),
  /** All approval rows for this package (chronological). */
  approvals: z.array(approvalRowSchema),
  /** Items currently flagged as ambiguous. */
  ambiguities: z.array(ambiguityItemSchema),
  /**
   * Whether the package is eligible for approved → released transition.
   * True iff approvals.length ≥ 1 (locked decision §2).
   */
  releaseEligible: z.boolean(),
})

export type TrustSummarySchema = z.infer<typeof trustSummarySchema>
